import { useEffect, useRef, useState } from "react";
import { Button, Card } from "react-bootstrap";
import EditProgramModal from "./components/EditProgramModal";
import { fetchAndSetPrograms, handleEdoProgramsFilterChange, resetEdoProgramFilterInputs } from "./EdoPrograms.helpers";
import ProgramSignal, { fundingProgramsSignal } from "signals/Program.signal";
import { fetchAndSetProgram } from "../EdoProgramDetail/EdoProgramDetail.helpers";
import SimpleTable from "components/global/SimpleTable";
import SimpleTableFilterDropdown from "components/global/SimpleTableFilterDropdown";
import { useHistory } from "react-router";
import BreadCrumbWrapper from "components/global/BreadCrumbs/BreadCrumbs";
import userAccountSignal from "signals/UserAccount.signal";
import { checkEdoPermissionsAcrossPrograms } from "../../../../libs/edoPermissions";

const HEADERS = [
  {
    key: 'id',
    title: 'ID',
    sortable: true,
  },
  {
    key: 'name',
    title: 'Name',
    sortable: true,
  },
  {
    key: 'programType',
    title: 'Program Type',
  },
  {
    key: 'platformType',
    title: 'Platform Type',
  },
  {
    key: 'transactionLeverageRatio',
    title: 'Transaction Leverage Ratio',
  },
  {
    key: 'action',
    title: 'Action',
  },
];

const EdoPrograms = () => {
  const { sort, dropdownFilters } = ProgramSignal.value;
  const history = useHistory();
  const [canView, setCanView] = useState(false);
  const [canInsert, setCanInsert] = useState(false);
  const [permissionsChecked, setPermissionsChecked] = useState(false);
  const loadIdRef = useRef(0);

  useEffect(() => {
    const loadId = ++loadIdRef.current;
    const load = async () => {
      setPermissionsChecked(false);
      const userData = userAccountSignal.value.userData;
      if (!userData) {
        return;
      }
      await fetchAndSetPrograms();
      if (loadId !== loadIdRef.current) return;
      const programs = fundingProgramsSignal.value;
      const permissions = await checkEdoPermissionsAcrossPrograms(
        programs,
        ['canView', 'canInsert']
      );
      if (loadId !== loadIdRef.current) return;
      setCanView(permissions.canView);
      setCanInsert(permissions.canInsert);
      setPermissionsChecked(true);
    };
    load();
  }, [history.location.search, userAccountSignal.value.userData]);

  if (!permissionsChecked) {
    return (
      <BreadCrumbWrapper>
        <Card className="rounded-0">
          <Card.Body className="text-center">Loading...</Card.Body>
        </Card>
      </BreadCrumbWrapper>
    );
  }

  if (!canView) {
    return (
      <BreadCrumbWrapper>
        <Card className="rounded-0">
          <Card.Body>
            <h3 className="text-center">Access Denied</h3>
            <p className="text-center">
              You do not have permission to view programs.
            </p>
          </Card.Body>
        </Card>
      </BreadCrumbWrapper>
    );
  }

  return (
    <BreadCrumbWrapper>
      <Card className="rounded-0">
        <Card.Body>
          <h3 className="text-center">Programs</h3>
          <hr />
          <div className="d-flex flex-row justify-content-between mb-16">
            <SimpleTableFilterDropdown
              filtersState={dropdownFilters}
              onChange={({ name, value }) =>
                handleEdoProgramsFilterChange(name, value)
              }
              onApply={() => {
                fetchAndSetPrograms();
              }}
              onClearFilterInputs={resetEdoProgramFilterInputs}
              filters={[
                {
                  type: 'integer',
                  label: 'ID',
                  key: 'id',
                },
              ]}
            />
            {canInsert && (
              <div>
                <Button
                  className="rounded"
                  onClick={() => history.push('/edo/programs/add-program')}
                >
                  New Program
                </Button>
              </div>
            )}
          </div>
          <SimpleTable
            headers={HEADERS}
            sort={sort}
            onClick={(index) => {
              const fundingProgram = fundingProgramsSignal.value[index];
              fetchAndSetProgram(fundingProgram.id);
            }}
            onSort={() => {
              fetchAndSetPrograms();
            }}
            rows={fundingProgramsSignal.value.map((fundingProgram) => ({
              id: fundingProgram.id,
              name: fundingProgram.name,
              programType: fundingProgram.referenceProgram.programName,
              platformType: fundingProgram.platform.name,
              transactionLeverageRatio: fundingProgram.stats.loanAmount
                ? (
                    fundingProgram.stats.borrowerLoanAmount /
                    fundingProgram.stats.loanAmount
                  ).toLocaleString()
                : '-',
              action: ({ key }) => (
                <td className="text-right" key={key}>
                  <Button
                    className="rounded"
                    size="sm"
                    variant="outline-dark"
                    onClick={() => {
                      history.push(`/edo/programs/${fundingProgram.id}`);
                    }}
                  >
                    Detail
                  </Button>
                </td>
              ),
            }))}
          />
        </Card.Body>
      </Card>
      <EditProgramModal />
    </BreadCrumbWrapper>
  );
};

export default EdoPrograms;
